import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import supabase from "../supabase";
import { generatePostcard, generatePostcardBack, THEMES, BACK_ZONES } from "../utils/postcardGenerator";
import { useLang } from "../useLang";
import PostcardViewer3D from "../components/PostcardViewer3D";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { useLoginModal } from "../LoginModalContext";

const ALLOWED_ROLES = ["CREATOR", "ORGANISATION", "ADMIN"];
const DRAFT_KEY = "postcard_draft";

function todayDateString() {
    return new Date().toISOString().slice(0, 10);
}

export default function Postcard() {
    const { lang } = useLang();
    const { openLoginModal } = useLoginModal();
    const navigate = useNavigate();

    const [albums, setAlbums] = useState([]);
    const [openAlbum, setOpenAlbum] = useState(null);
    const [albumPhotos, setAlbumPhotos] = useState([]);
    const [loadingPhotos, setLoadingPhotos] = useState(false);
    const [selected, setSelected] = useState(null);
    const [themeId, setThemeId] = useState(null);
    const [rendering, setRendering] = useState(false);
    const [showViewer3D, setShowViewer3D] = useState(false);
    const [showBack, setShowBack] = useState(true);
    const [frontImgSrc, setFrontImgSrc] = useState(null);
    const [backImgSrc, setBackImgSrc] = useState(null);

    const canvasRef = useRef(null);
    const backCanvasRef = useRef(null);
    const messageRef = useRef(null);
    const senderRef = useRef(null);
    const recipientRef = useRef(null);
    const addressRef = useRef(null);

    const [message, setMessage] = useState("");
    const [senderName, setSenderName] = useState("");
    const [recipientName, setRecipientName] = useState("");
    const [address, setAddress] = useState("");
    const [pulseZone, setPulseZone] = useState(null);

    const [authUser, setAuthUser] = useState(null);
    const [checkingAuth, setCheckingAuth] = useState(true);
    const [senderDisplayName, setSenderDisplayName] = useState("");
    const [recipientEmail, setRecipientEmail] = useState("");
    const [sendStatus, setSendStatus] = useState(""); // '' | 'sending' | 'sent' | 'quota' | 'error'
    const [sendMsg, setSendMsg] = useState("");
    const [remainingToday, setRemainingToday] = useState(null);
    const [showSendModal, setShowSendModal] = useState(false);
    const [showContinuePrompt, setShowContinuePrompt] = useState(false);

    const loadAuth = async () => {
        const { data: sessionData } = await supabase.auth.getSession();
        if (!sessionData.session) {
            setCheckingAuth(false);
            setAuthUser(null);
            return;
        }
        const { data: userRow } = await supabase
            .from("users")
            .select("username, first_name, last_name, role, postcard_daily_limit, postcard_daily_sends, postcard_last_send_date")
            .eq("user_id", sessionData.session.user.id)
            .single();
        if (userRow) {
            setAuthUser(userRow);
            const fullName = `${userRow.first_name || ""} ${userRow.last_name || ""}`.trim() || userRow.username;
            setSenderDisplayName(fullName);

            const limit = userRow.postcard_daily_limit || 5;
            const today = todayDateString();
            const usedToday = userRow.postcard_last_send_date === today ? userRow.postcard_daily_sends || 0 : 0;
            setRemainingToday(limit - usedToday);
        }
        setCheckingAuth(false);
    };

    useEffect(() => {
        loadAuth();

        const { data: listener } = supabase.auth.onAuthStateChange((event) => {
            if (event === "SIGNED_IN") {
                loadAuth();
            } else if (event === "SIGNED_OUT") {
                setAuthUser(null);
                setSenderDisplayName("");
                setRemainingToday(null);
            }
        });

        return () => listener.subscription.unsubscribe();
    }, []);

    useEffect(() => {
        supabase
            .from("albums")
            .select("folder, region, cover_url, title, location, date, created_by")
            .eq("archived", false)
            .eq("postcard_eligible", true)
            .order("date", { ascending: false })
            .then(({ data, error }) => {
                if (!error) setAlbums(data || []);
            });
    }, []);

    useEffect(() => {
        if (!selected || !themeId || !canvasRef.current) return;
        setRendering(true);
        generatePostcard({
            canvas: canvasRef.current,
            photoUrl: selected.photoUrl,
            location: selected.location,
            date: selected.date,
            title: selected.title,
            creator: selected.creator,
            themeId,
        })
            .then(() => setFrontImgSrc(canvasRef.current.toDataURL("image/png")))
            .finally(() => setRendering(false));
    }, [selected, themeId]);

    useEffect(() => {
        if (!selected || !themeId || !backCanvasRef.current) return;
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => {
            generatePostcardBack(backCanvasRef.current, {
                message,
                senderName,
                recipientName,
                address,
                creator: selected.creator,
                date: selected.date,
                stampImg: img,
                themeId,
            });
            setBackImgSrc(backCanvasRef.current.toDataURL("image/png"));
        };
        img.src = "/winston-smiley-white.svg";
    }, [selected, themeId, message, senderName, recipientName, address]);

    const openAlbumPhotos = async (album) => {
        setOpenAlbum(album);
        setLoadingPhotos(true);
        const { data: eligible } = await supabase.from("postcard_photos").select("photo_url").eq("album_folder", album.folder);
        setAlbumPhotos((eligible || []).map((row) => row.photo_url));
        setLoadingPhotos(false);
    };

    const choosePhoto = (photoUrl) => {
        setSelected({
            photoUrl,
            title: openAlbum.title,
            location: openAlbum.location,
            date: openAlbum.date,
            creator: openAlbum.created_by,
            folder: openAlbum.folder,
        });
    };

    const downloadCanvas = (canvas, suffix) => {
        const link = document.createElement("a");
        link.download = `winston-postcard-${selected.folder}-${suffix}.png`;
        link.href = canvas.toDataURL("image/png");
        link.click();
    };

    const focusZone = (zoneKey, ref) => {
        setPulseZone(zoneKey);
        ref.current?.focus();
        ref.current?.scrollIntoView({ behavior: "smooth", block: "center" });
        setTimeout(() => setPulseZone(null), 900);
    };

    const saveDraftAndLogin = () => {
        const draft = { selected, themeId, message, senderName, recipientName, address, senderDisplayName, recipientEmail };
        sessionStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
        openLoginModal();
    };

    const handleSendEmail = async () => {
        if (!recipientEmail.trim()) {
            setSendStatus("error");
            setSendMsg(lang === "fr" ? "Entre un email destinataire." : "Enter a recipient email.");
            return;
        }
        setSendStatus("sending");
        setSendMsg("");

        const { data, error } = await supabase.functions.invoke("send-postcard", {
            body: {
                recipientEmail,
                recipientName,
                message,
                frontImageBase64: canvasRef.current.toDataURL("image/png"),
                backImageBase64: backCanvasRef.current.toDataURL("image/png"),
                senderDisplayName,
            },
        });

        if (error) {
            console.error("send-postcard error:", error);
            let body = null;
            try {
                body = error.context ? await error.context.json() : null;
            } catch (_) { }

            if (body?.error === "quota_exceeded") {
                setSendStatus("quota");
                setRemainingToday(0);
                setSendMsg(lang === "fr" ? `Limite atteinte (${body.limit}/jour).` : `Daily limit reached (${body.limit}/day).`);
            } else {
                setSendStatus("error");
                setSendMsg(body?.error || error.message || (lang === "fr" ? "Échec de l'envoi." : "Send failed."));
            }
            return;
        }

        setSendStatus("sent");
        setRemainingToday(data?.remaining ?? null);
        setSendMsg(lang === "fr" ? "Carte postale envoyée !" : "Postcard sent!");
        setRecipientEmail("");

        setTimeout(() => {
            setShowContinuePrompt(true);
        }, 900);
    };

    const handleContinueSending = () => {
        setShowContinuePrompt(false);
        setSendStatus("");
        setSendMsg("");
    };

    const handleStopSending = () => {
        setShowContinuePrompt(false);
        setShowSendModal(false);
        setSendStatus("");
        setSendMsg("");
    };

    const campaignAlbum = albums.find((a) => a.folder === "campaign" || a.region === "campaign");
    const regularAlbums = albums.filter((a) => a !== campaignAlbum);

    const step = !openAlbum && !selected ? 1 : openAlbum && !selected ? 2 : !themeId ? 3 : 4;

    return (
        <div style={{ background: "#0e1013", minHeight: "100vh", display: "flex", flexDirection: "column" }}>
            <Navbar variant="dark" />

            <div style={{ padding: "60px 40px", maxWidth: 1100, margin: "0 auto", width: "100%", flex: 1 }}>
                <h1 style={pageTitle}>{lang === "fr" ? "Générateur de cartes postales" : "Postcard Generator"}</h1>
                <p style={stepIndicator}>
                    {lang === "fr" ? "Étape" : "Step"} {step}/4 —{" "}
                    {step === 1
                        ? lang === "fr" ? "Choisir un album" : "Choose an album"
                        : step === 2
                            ? lang === "fr" ? "Choisir une photo" : "Choose a photo"
                            : step === 3
                                ? lang === "fr" ? "Choisir un style" : "Choose a style"
                                : lang === "fr" ? "Écrire & partager" : "Write & share"}
                </p>

                {step === 1 && (
                    <>
                        {campaignAlbum && (
                            <div style={{ marginBottom: 40 }}>
                                <h2 style={sectionTitle}>{lang === "fr" ? "CAMPAGNE" : "CAMPAIGN"}</h2>
                                <div style={grid}>
                                    <AlbumTile album={campaignAlbum} onClick={() => openAlbumPhotos(campaignAlbum)} />
                                </div>
                            </div>
                        )}
                        <h2 style={sectionTitle}>{lang === "fr" ? "TOUS LES ALBUMS" : "ALL ALBUMS"}</h2>
                        <div style={grid}>
                            {regularAlbums.map((a) => (
                                <AlbumTile key={a.folder} album={a} onClick={() => openAlbumPhotos(a)} />
                            ))}
                        </div>
                    </>
                )}

                {step === 2 && (
                    <div>
                        <button onClick={() => setOpenAlbum(null)} className="wlink" style={{ ...linkSecondary, marginBottom: 24 }}>
                            ← {lang === "fr" ? "Retour aux albums" : "Back to albums"}
                        </button>
                        <h2 style={sectionTitle}>{openAlbum.title}</h2>
                        {loadingPhotos ? (
                            <p style={bodyText}>{lang === "fr" ? "Chargement..." : "Loading..."}</p>
                        ) : albumPhotos.length === 0 ? (
                            <p style={bodyText}>{lang === "fr" ? "Aucune photo disponible pour cet album." : "No photos available for this album."}</p>
                        ) : (
                            <div style={grid}>
                                {albumPhotos.map((url) => (
                                    <div key={url} onClick={() => choosePhoto(url)} style={{ ...photoTileBg, backgroundImage: `url(${url})` }} />
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {step === 3 && (
                    <div>
                        <button onClick={() => setSelected(null)} className="wlink" style={{ ...linkSecondary, marginBottom: 24 }}>
                            ← {lang === "fr" ? "Changer de photo" : "Choose another photo"}
                        </button>
                        <h2 style={sectionTitle}>{lang === "fr" ? "STYLE DE CARTE" : "CARD STYLE"}</h2>
                        <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
                            {Object.values(THEMES).map((t) => (
                                <div
                                    key={t.id}
                                    onClick={() => setThemeId(t.id)}
                                    style={{ cursor: "pointer", width: 220, borderRadius: 8, overflow: "hidden", border: "1px solid rgba(244,241,236,0.15)" }}
                                >
                                    <div style={{ height: 140, background: t.bg }} />
                                    <div style={{ padding: 12, background: "rgba(244,241,236,0.04)" }}>
                                        <p style={{ ...bodyText, fontWeight: 500, marginBottom: 2 }}>{t.label}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {step === 4 && (
                    <div>
                        <div style={{ display: "flex", gap: 24, marginBottom: 24, flexWrap: "wrap" }}>
                            <button onClick={() => setThemeId(null)} className="wlink" style={linkSecondary}>
                                ← {lang === "fr" ? "Changer de style" : "Change style"}
                            </button>
                            <button onClick={() => setShowViewer3D((v) => !v)} className="wlink" style={linkSecondary}>
                                {showViewer3D ? (lang === "fr" ? "Voir à plat" : "Flat view") : lang === "fr" ? "Voir en 3D" : "View in 3D"}
                            </button>
                        </div>

                        <canvas ref={canvasRef} width={1200} height={800} style={{ display: "none" }} />
                        <canvas ref={backCanvasRef} width={1200} height={800} style={{ display: "none" }} />

                        {showViewer3D ? (
                            <>
                                <PostcardViewer3D frontCanvas={canvasRef.current} backCanvas={backCanvasRef.current} />
                                <p style={hintText}>{lang === "fr" ? "Glisse pour faire pivoter la carte" : "Drag to rotate the card"}</p>
                            </>
                        ) : (
                            <div style={{ maxWidth: 720, margin: "0 auto" }}>
                                <div style={{ display: "flex", gap: 20, justifyContent: "center", marginBottom: 12 }}>
                                    <button onClick={() => setShowBack(false)} disabled={!showBack} className="wlink" style={linkSecondary}>
                                        {lang === "fr" ? "Recto" : "Front"}
                                    </button>
                                    <button onClick={() => setShowBack(true)} disabled={showBack} className="wlink" style={linkSecondary}>
                                        {lang === "fr" ? "Verso" : "Back"}
                                    </button>
                                </div>

                                <div style={{ position: "relative" }}>
                                    {!showBack && frontImgSrc && (
                                        <img src={frontImgSrc} alt="Postcard front" style={{ width: "100%", display: "block", borderRadius: 4 }} />
                                    )}
                                    {showBack && backImgSrc && (
                                        <>
                                            <img src={backImgSrc} alt="Postcard back" style={{ width: "100%", display: "block", borderRadius: 4 }} />
                                            {Object.entries(BACK_ZONES).map(([key, z]) => {
                                                const refMap = { message: messageRef, sender: senderRef, recipient: recipientRef, address: addressRef };
                                                return (
                                                    <div
                                                        key={key}
                                                        onClick={() => focusZone(key, refMap[key])}
                                                        style={{
                                                            position: "absolute",
                                                            left: `${z.left * 100}%`,
                                                            top: `${z.top * 100}%`,
                                                            width: `${z.width * 100}%`,
                                                            height: `${z.height * 100}%`,
                                                            cursor: "pointer",
                                                            border: pulseZone === key ? "2px solid #f4f1ec" : "1px dashed transparent",
                                                            transition: "border-color 0.2s ease",
                                                        }}
                                                        onMouseEnter={(e) => (e.currentTarget.style.border = "1px dashed rgba(244,241,236,0.5)")}
                                                        onMouseLeave={(e) => (e.currentTarget.style.border = pulseZone === key ? "2px solid #f4f1ec" : "1px dashed transparent")}
                                                    />
                                                );
                                            })}
                                        </>
                                    )}
                                </div>
                            </div>
                        )}

                        <div style={{ marginTop: 28, display: "flex", flexDirection: "column", gap: 12, maxWidth: 720, marginLeft: "auto", marginRight: "auto" }}>
                            <Field label={lang === "fr" ? "Message" : "Message"}>
                                <textarea ref={messageRef} value={message} onChange={(e) => setMessage(e.target.value)} rows={3} style={inputStyle} />
                            </Field>
                            <Field label={lang === "fr" ? "Expéditeur (de la part de)" : "Sender (from)"}>
                                <input ref={senderRef} value={senderName} onChange={(e) => setSenderName(e.target.value)} style={inputStyle} />
                            </Field>
                            <Field label={lang === "fr" ? "Destinataire" : "Recipient"}>
                                <input ref={recipientRef} value={recipientName} onChange={(e) => setRecipientName(e.target.value)} style={inputStyle} />
                            </Field>
                            <Field label={lang === "fr" ? "Adresse (une ligne par entrée)" : "Address (one line per field)"}>
                                <textarea ref={addressRef} value={address} onChange={(e) => setAddress(e.target.value)} rows={3} style={inputStyle} />
                            </Field>
                        </div>

                        <div style={{ marginTop: 24, display: "flex", gap: 24, justifyContent: "center", flexWrap: "wrap" }}>
                            <button onClick={() => downloadCanvas(canvasRef.current, "front")} disabled={rendering} className="wlink" style={linkPrimary}>
                                {lang === "fr" ? "Télécharger le recto" : "Download front"}
                            </button>
                            <button onClick={() => downloadCanvas(backCanvasRef.current, "back")} disabled={rendering} className="wlink" style={linkSecondary}>
                                {lang === "fr" ? "Télécharger le verso" : "Download back"}
                            </button>
                        </div>
                        <div style={{ marginTop: 40, maxWidth: 720, marginLeft: "auto", marginRight: "auto", borderTop: "1px solid rgba(244,241,236,0.12)", paddingTop: 28, textAlign: "center" }}>
                            {checkingAuth ? (
                                <p style={bodyText}>{lang === "fr" ? "Vérification..." : "Checking..."}</p>
                            ) : !authUser ? (
                                <>
                                    <p style={authStatusStyle}>
                                        <span style={{ ...statusDot, background: "#e08c8c" }} />
                                        {lang === "fr" ? "Non connecté" : "Not signed in"}
                                    </p>
                                    <button onClick={saveDraftAndLogin} className="wlink" style={linkPrimary}>
                                        {lang === "fr" ? "Se connecter pour l'envoyer à quelqu'un" : "Log in to send it to someone directly"}
                                    </button>
                                </>
                            ) : !ALLOWED_ROLES.includes((authUser.role || "").toUpperCase()) ? (
                                <>
                                    <p style={authStatusStyle}>
                                        <span style={{ ...statusDot, background: "#e0b666" }} />
                                        {lang === "fr" ? `Connecté(e) : ${authUser.username}` : `Signed in: ${authUser.username}`}
                                    </p>
                                    <p style={bodyText}>
                                        {lang === "fr" ? "Ton compte n'a pas accès à l'envoi de cartes postales." : "Your account doesn't have access to sending postcards."}
                                    </p>
                                </>
                            ) : (
                                <>
                                    <p style={authStatusStyle}>
                                        <span style={{ ...statusDot, background: "#8fbf8f" }} />
                                        {lang === "fr" ? `Connecté(e) : ${authUser.username}` : `Signed in: ${authUser.username}`}
                                    </p>
                                    <button onClick={() => setShowSendModal(true)} className="wlink" style={linkPrimary}>
                                        {lang === "fr" ? "Envoyer cette carte à quelqu'un" : "Send this postcard to someone"}
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {showSendModal && (
                <div
                    style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
                    onClick={() => (showContinuePrompt ? handleStopSending() : setShowSendModal(false))}
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        style={{ background: "#16181c", borderRadius: 8, padding: 28, maxWidth: 420, width: "100%", display: "flex", flexDirection: "column", gap: 14 }}
                    >
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                            <h3 style={{ ...pageTitle, fontSize: 18, marginBottom: 0 }}>
                                {lang === "fr" ? "Envoyer par courriel" : "Send by email"}
                            </h3>
                            {remainingToday !== null && (
                                <span style={remainingBadge}>
                                    {lang === "fr" ? `${remainingToday}/5 restants` : `${remainingToday}/5 left`}
                                </span>
                            )}
                        </div>

                        {showContinuePrompt ? (
                            <>
                                <p style={{ ...bodyText, fontSize: 13 }}>
                                    {lang === "fr" ? "Envoyer une autre carte postale ?" : "Send another postcard?"}
                                </p>
                                <div style={{ display: "flex", gap: 20 }}>
                                    <button onClick={handleContinueSending} className="wlink" style={linkPrimary}>
                                        {lang === "fr" ? "Oui, continuer" : "Yes, continue"}
                                    </button>
                                    <button onClick={handleStopSending} className="wlink" style={linkSecondary}>
                                        {lang === "fr" ? "Non, terminer" : "No, I'm done"}
                                    </button>
                                </div>
                            </>
                        ) : (
                            <>
                                {authUser && (
                                    <p style={{ ...bodyText, fontSize: 13, opacity: 0.7, margin: 0 }}>
                                        {lang === "fr" ? `Connecté en tant que ${authUser.username}` : `Signed in as ${authUser.username}`}
                                    </p>
                                )}

                                <Field label={lang === "fr" ? "Ton nom (affiché au destinataire)" : "Your name (shown to recipient)"}>
                                    <input value={senderDisplayName} onChange={(e) => setSenderDisplayName(e.target.value)} maxLength={60} style={inputStyle} />
                                </Field>
                                <Field label={lang === "fr" ? "Email du destinataire" : "Recipient email"}>
                                    <input
                                        type="email"
                                        value={recipientEmail}
                                        onChange={(e) => setRecipientEmail(e.target.value)}
                                        placeholder="ami@example.com"
                                        style={inputStyle}
                                        autoFocus
                                    />
                                </Field>

                                <button onClick={handleSendEmail} disabled={sendStatus === "sending" || remainingToday === 0} className="wlink" style={linkPrimary}>
                                    {sendStatus === "sending"
                                        ? lang === "fr" ? "Envoi..." : "Sending..."
                                        : lang === "fr" ? "Envoyer la carte postale" : "Send postcard"}
                                </button>

                                {remainingToday === 0 && (
                                    <p style={{ ...bodyText, fontSize: 12, opacity: 0.6 }}>
                                        {lang === "fr"
                                            ? "Limite atteinte — télécharge la carte pour l'envoyer manuellement."
                                            : "Limit reached — download the postcard to send it manually."}
                                    </p>
                                )}

                                {sendMsg && (
                                    <p style={{ ...bodyText, fontSize: 12, color: sendStatus === "sent" ? "#8fbf8f" : sendStatus === "quota" ? "#e0b666" : "#e08c8c" }}>
                                        {sendMsg}
                                    </p>
                                )}

                                {(sendStatus === "quota" || remainingToday === 0) && (
                                    <button onClick={() => navigate("/admin/dashboard")} className="wlink" style={linkSecondary}>
                                        {lang === "fr" ? "Demander plus d'envois →" : "Request more sends →"}
                                    </button>
                                )}

                                <button onClick={() => setShowSendModal(false)} className="wlink" style={{ ...linkSecondary, marginTop: 4 }}>
                                    {lang === "fr" ? "Fermer" : "Close"}
                                </button>
                            </>
                        )}
                    </div>
                </div>
            )}

            <Footer />
        </div>
    );
}

function Field({ label, children }) {
    return (
        <div>
            <label style={{ ...bodyText, fontSize: 11, letterSpacing: 1, textTransform: "uppercase", opacity: 0.5, display: "block", marginBottom: 4 }}>
                {label}
            </label>
            {children}
        </div>
    );
}

function AlbumTile({ album, onClick }) {
    return (
        <div onClick={onClick} style={{ ...photoTileBg, backgroundImage: `url(${album.cover_url})`, position: "relative" }}>
            <div
                style={{
                    position: "absolute",
                    bottom: 0,
                    left: 0,
                    right: 0,
                    padding: "8px",
                    background: "linear-gradient(transparent, rgba(0,0,0,0.7))",
                    color: "#f4f1ec",
                    fontFamily: "Helvetica Neue, Arial, sans-serif",
                    fontWeight: 300,
                    fontSize: 12,
                }}
            >
                {album.title}
            </div>
        </div>
    );
}

const UI_FONT = 'Helvetica Neue, Arial, sans-serif';
const color = '#f4efe4';

const pageTitle = {  background: 'none', border: 'none', padding: 0,
                    fontFamily: UI_FONT, fontWeight: 300,
                    fontSize: 'clamp(30px, 6vw, 52px)', letterSpacing: '-0.01em',
                    lineHeight: 1, color,};
const stepIndicator = { fontFamily: UI_FONT, fontWeight: 300, color: "rgba(244,241,236,0.45)", fontSize: 12, letterSpacing: 1, textTransform: "uppercase", marginBottom: 32 };
const sectionTitle = { fontFamily: UI_FONT, fontWeight: 300, color: "rgba(244,241,236,0.6)", fontSize: 13, letterSpacing: 2, textTransform: "uppercase", marginBottom: 12 };
const bodyText = { fontFamily: UI_FONT, fontWeight: 300, color: "#f4f1ec", fontSize: 14 };
const hintText = { fontFamily: UI_FONT, fontWeight: 300, color: "rgba(244,241,236,0.4)", fontSize: 12, textAlign: "center", marginTop: 8 };
const grid = { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 16 };
const photoTileBg = { cursor: "pointer", aspectRatio: "1", backgroundSize: "cover", backgroundPosition: "center", borderRadius: 4 };
const inputStyle = {
    padding: "12px 14px",
    fontFamily: UI_FONT,
    fontWeight: 300,
    fontSize: 14,
    background: "rgba(244,241,236,0.05)",
    border: "1px solid rgba(244,241,236,0.15)",
    borderRadius: 4,
    color: "#f4f1ec",
    outline: "none",
    resize: "vertical",
    width: "100%",
    boxSizing: "border-box",
};

// Plus de pilules pleines/bordées : du texte nu, souligné au survol
// (classe .wlink, portée par le <style> toujours monté dans Navbar.js).
const linkPrimary = {
    fontFamily: UI_FONT,
    fontWeight: 300,
    fontSize: 15,
    letterSpacing: "-0.01em",
    background: "none",
    border: "none",
    padding: 0,
    color: "#f4f1ec",
    cursor: "pointer",
};
const linkSecondary = {
    ...linkPrimary,
    fontSize: 14,
    color: "rgba(244,241,236,0.55)",
};
const remainingBadge = {
    fontFamily: UI_FONT,
    fontWeight: 300,
    fontSize: 11,
    color: "rgba(244,241,236,0.5)",
};

const authStatusStyle = {
    fontFamily: UI_FONT,
    fontWeight: 300,
    fontSize: 12,
    color: "rgba(244,241,236,0.7)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    marginBottom: 14,
};

const statusDot = {
    display: "inline-block",
    width: 7,
    height: 7,
    borderRadius: "50%",
};